import * as React from "react";
import { useCallback, useMemo } from "react";
import { Formik } from "formik";
import capitalize from "lodash/capitalize";
import UserNotification from "util/UserNotification";
import useHistory from "routing/useHistory";
import ReportRoutes from "report/ReportRoutes";
import { toBackendWidgetRef } from "report/report-creation/content/types";
import ParameterBinding from "views/logic/parameters/ParameterBinding";
import validateReportForm from "report/report-creation/validateReportForm";
import ReportFormContent from "report/report-creation/ReportFormContent";
import WindowLeaveMessage from "report/report-creation/WindowLeaveMessage";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import { CurrentUserStore } from "stores/users/CurrentUserStore";
import _saveReport from "./saveReport";
const normalizeLayout = (layout) => ({
  format: layout == null ? void 0 : layout.format,
  orientation: layout == null ? void 0 : layout.orientation,
  page_size: layout == null ? void 0 : layout.pageSize,
  print_header: layout == null ? void 0 : layout.printHeader,
  header: layout == null ? void 0 : layout.header,
  print_footer: layout == null ? void 0 : layout.printFooter,
  footer: layout == null ? void 0 : layout.footer,
  print_page_numbers: layout == null ? void 0 : layout.printPageNumbers,
  print_toc: layout == null ? void 0 : layout.printToc
});
const normalizeParameterValues = (parameterValues) => !parameterValues ? {} : Object.fromEntries(
  Object.entries(parameterValues).map(([key, value]) => [key, ParameterBinding.forValue(value)])
);
export const normalizeReport = ({
  delivery,
  description,
  hideWidgetDescription,
  hideWidgetQuery,
  layout,
  logo,
  parameterValues,
  positions,
  subtitle,
  timezone,
  title,
  widgets,
  share_request: shareRequest
}) => ({
  delivery,
  description,
  hide_widget_description: hideWidgetDescription,
  hide_widget_query: hideWidgetQuery,
  parameter_values: normalizeParameterValues(parameterValues),
  layout: normalizeLayout(layout),
  logo,
  positions: (positions == null ? void 0 : positions.length) > 0 ? positions : widgets.map((widget, idx) => ({ dashboard_widget_id: widget.widgetId, col: 1, row: idx + 1 })),
  subtitle,
  timezone,
  title,
  widgets: widgets.map(toBackendWidgetRef),
  share_request: shareRequest
});
const saveReport = (formState) => {
  const { id, ...report } = formState;
  const normalizedReport = normalizeReport(report);
  return _saveReport(id, normalizedReport).then((newReport) => {
    UserNotification.success(`Report ${id ? "updated" : "created"} successfully`);
    if (!id) {
      CurrentUserStore.reload();
    }
    return newReport;
  }).catch((e) => {
    UserNotification.error(`There was an error ${id ? "updating" : "creating"} the report: ${e.message}`);
  });
};
const ReportForm = ({ report, action, ignoredWindowLeaveRoutes }) => {
  const history = useHistory();
  const onCancel = useCallback(() => history.push(ReportRoutes.OVERVIEW), [history]);
  const pathPrefix = "id" in report ? ReportRoutes.contents(report.id) : ReportRoutes.NEW;
  const title = useMemo(() => `${capitalize(action)} Report`, [action]);
  const currentUser = useCurrentUser();
  const editAllowed = action === "create" || isPermitted(currentUser.permissions, `report:update:${report.id}`);
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: report,
      onSubmit: saveReport,
      validateOnBlur: false,
      validate: validateReportForm
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(WindowLeaveMessage, { ignoredRoutes: ignoredWindowLeaveRoutes }), /* @__PURE__ */ React.createElement(
      ReportFormContent,
      {
        action,
        pathPrefix,
        onCancel,
        title,
        editAllowed
      }
    ))
  );
};
export default ReportForm;
